import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Phone, MapPin, User, X, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { logger } from '../utils/logger';
import { useLocalization } from '../hooks/useLocalization';
import { complianceService } from '../services';
import { useTranslation } from 'react-i18next';

interface EmergencyButtonProps {
  patientId?: string;
  patientName?: string;
  location?: string;
}

const EmergencyButton: React.FC<EmergencyButtonProps> = ({ 
  patientId, 
  patientName, 
  location 
}) => {
  const { t } = useTranslation('common');
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [emergencyType, setEmergencyType] = useState('');
  const [description, setDescription] = useState('');
  const [showToast, setShowToast] = useState(false);
  const { currentCountry, emergency } = useLocalization();

  const handleEmergencyClick = () => {
    setShowEmergencyModal(true);
  };

  const handleEmergencyCall = async () => {
    setIsCalling(true);
    
    try {
      await complianceService.notifyEmergency({
        type: emergencyType || t('emergencyButton.btnTitle'),
        description: description || t('emergencyButton.defaultDesc'),
        location: location || t('emergencyButton.locationUnavailable'),
        patientId: patientId
      });

      window.open(`tel:${emergency.samu}`, '_self');
      
      setTimeout(() => {
        setShowEmergencyModal(false);
        setIsCalling(false);
        setEmergencyType('');
        setDescription('');
      }, 3000);
      
    } catch (error) {
      logger.error('Erreur lors de la notification d\'urgence:', error);
      setIsCalling(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    }
  };

  const handleCancel = () => {
    setShowEmergencyModal(false);
    setEmergencyType('');
    setDescription('');
  };

  // The floating button portal content
  const PortalContent = (
    <>
      <div className="fixed bottom-24 right-4 lg:bottom-8 lg:right-8 z-[100] group">
        <motion.button
          whileHover={{ scale: 1.15, rotate: 12 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleEmergencyClick}
          className="relative flex items-center justify-center w-16 h-16 bg-rose-600 text-white rounded-2xl shadow-[0_20px_50px_rgba(225,29,72,0.4)] focus:outline-none focus:ring-8 focus:ring-rose-500/20 border-4 border-white/60 backdrop-blur-xl group overflow-hidden"
          title={t('emergencyButton.btnTitle')}
          aria-label={t('emergencyButton.btnAriaLabel')}
        >
          {/* Intense pulse effect */}
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
          <div className="absolute inset-0 rounded-2xl border-4 border-rose-400 animate-ping opacity-30"></div>
          <AlertTriangle size={28} strokeWidth={2.5} className="relative z-10" />
        </motion.button>
      </div>

      <AnimatePresence>
        {showEmergencyModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"
              onClick={handleCancel}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg bg-white/80 backdrop-blur-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-white/80"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header Gradient */}
              <div className="bg-gradient-to-br from-rose-600 via-rose-700 to-rose-900 p-8 text-white relative">
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-bl-full pointer-events-none blur-2xl"></div>
                
                <button
                  onClick={handleCancel}
                  className="absolute top-6 right-6 w-10 h-10 rounded-2xl bg-white/20 hover:bg-white hover:text-rose-600 transition-all flex items-center justify-center border border-white/20 backdrop-blur-md"
                  disabled={isCalling}
                >
                  <X size={20} />
                </button>
                
                <div className="flex items-center gap-6 mb-8 relative z-10">
                  <div className="w-20 h-20 bg-white rounded-[2rem] flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.3)] transform -rotate-12 group-hover:rotate-0 transition-transform duration-700">
                    <AlertTriangle className="w-10 h-10 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold italic tracking-tighter uppercase mb-1 leading-none">
                      {t('emergencyButton.modalTitle')} <span className="text-rose-200">{t('emergencyButton.modalTitleAccent')}</span>
                    </h3>
                    <p className="text-rose-100 font-bold text-[10px] tracking-[0.3em] uppercase italic opacity-80">
                      {t('emergencyButton.modalSubtitle')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-10 space-y-10 relative z-10">
                {/* Infos patient Elite */}
                {(patientName || location) && (
                  <div className="bg-rose-500/5 rounded-[2.5rem] p-8 border border-rose-500/10 text-sm shadow-inner group/info">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {patientName && (
                        <div className="flex items-center text-rose-900 font-bold italic tracking-tighter text-xl truncate uppercase">
                          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm border border-rose-100">
                             <User className="w-5 h-5 text-rose-600" />
                          </div>
                          {patientName}
                        </div>
                      )}
                      {location && (
                        <div className="flex items-center text-rose-900 font-bold italic tracking-tighter text-base opacity-70">
                          <MapPin className="w-5 h-5 mr-3 text-rose-600" /> {location}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Formulaire */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 px-1">
                      {t('emergencyButton.form.typeLabel')}
                    </label>
                    <select
                      value={emergencyType}
                      onChange={(e) => setEmergencyType(e.target.value)}
                      className="w-full bg-slate-50/50 backdrop-blur-md border-2 border-slate-100 rounded-[1.2rem] px-5 py-4 text-slate-800 font-bold italic text-sm focus:ring-4 focus:ring-rose-500/10 focus:border-rose-300 outline-none transition-all shadow-inner"
                    >
                      <option value="">{t('emergencyButton.form.typeSelect')}</option>
                      <option value="cardiac">{t('emergencyButton.types.cardiac')}</option>
                      <option value="respiratory">{t('emergencyButton.types.respiratory')}</option>
                      <option value="unconscious">{t('emergencyButton.types.unconscious')}</option>
                      <option value="bleeding">{t('emergencyButton.types.bleeding')}</option>
                      <option value="allergic">{t('emergencyButton.types.allergic')}</option>
                      <option value="other">{t('emergencyButton.types.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 px-1">
                      {t('emergencyButton.form.descLabel')}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('emergencyButton.form.descPlaceholder')}
                      className="w-full bg-slate-50/50 backdrop-blur-md border-2 border-slate-100 rounded-[1.2rem] px-5 py-4 text-slate-800 font-bold text-sm focus:ring-4 focus:ring-rose-500/10 focus:border-rose-300 outline-none transition-all resize-none shadow-inner"
                      rows={2}
                      disabled={isCalling}
                    />
                  </div>
                </div>

                {/* Warning Card */}
                <div className="bg-rose-600/5 border-l-4 border-rose-600 p-5 rounded-2xl flex gap-4 items-start shadow-sm">
                  <Activity className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-900 font-bold italic leading-relaxed tracking-tight">
                    {t('emergencyButton.warningText')}
                    <strong className="mx-2 text-rose-700 underline decoration-rose-300 underline-offset-4">{currentCountry.name} (SAMU : {emergency.samu})</strong>.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-2">
                  <button
                    onClick={handleCancel}
                    className="flex-1 py-5 bg-white border-2 border-slate-100 text-slate-500 rounded-2xl font-bold uppercase tracking-[0.15em] text-[10px] hover:bg-slate-50 transition-all active:scale-95"
                    disabled={isCalling}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleEmergencyCall}
                    disabled={isCalling || !emergencyType}
                    className="flex-3 py-5 bg-gradient-to-r from-rose-600 to-rose-700 text-white rounded-2xl font-bold uppercase tracking-[0.15em] text-xs shadow-2xl shadow-rose-600/40 hover:from-rose-700 hover:to-rose-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 active:scale-95 group"
                  >
                    {isCalling ? (
                      <>
                        <div className="w-5 h-5 rounded-full border-[3px] border-white/30 border-t-white animate-spin"></div>
                        {t('emergencyButton.calling')}
                      </>
                    ) : (
                      <>
                        <Phone size={18} className="group-hover:animate-shake" /> {t('emergencyButton.callBtn', { number: emergency.samu })}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Error Toast */}
      <AnimatePresence>
        {showToast && (
          <div className="fixed z-[300] bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-slate-900 border border-slate-700 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3"
            >
              <X className="w-5 h-5 text-rose-500 shrink-0" />
              <div className="text-sm font-medium">{t('emergencyButton.networkError', { number: emergency.samu })}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );

  return typeof document !== 'undefined' 
    ? ReactDOM.createPortal(PortalContent, document.body)
    : PortalContent; // Fallback pour le SSR si besoin
};

export default EmergencyButton; 
